import { type LoginBody, SessionInfo } from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiClientError, api, setCsrfToken } from './api';
import { OkResponse } from '@deizi/shared';

export const sessionKey = ['session'] as const;

async function fetchSession(): Promise<SessionInfo | null> {
  try {
    const s = await api.get('/auth/session', SessionInfo, { quiet401: true });
    setCsrfToken(s.csrfToken);
    return s;
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      setCsrfToken(null);
      return null;
    }
    throw err;
  }
}

export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: fetchSession,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** The signed-in user; only call inside the authenticated shell. */
export function useMe() {
  const { data } = useSession();
  if (!data) throw new Error('useMe outside an authenticated route');
  return data.user;
}

export function useIsAdmin(): boolean {
  return useMe().role === 'admin';
}

/** Applies a fresh session (login, invite, reset) to the cache and the API client. */
export function useApplySession() {
  const qc = useQueryClient();
  return (s: SessionInfo) => {
    setCsrfToken(s.csrfToken);
    qc.clear();
    qc.setQueryData(sessionKey, s);
  };
}

export function useLogin() {
  const apply = useApplySession();
  return useMutation({
    mutationFn: (body: LoginBody) => api.post('/auth/login', SessionInfo, body, { quiet401: true }),
    onSuccess: apply,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/auth/logout', OkResponse),
    onSettled: () => {
      setCsrfToken(null);
      qc.clear();
      qc.setQueryData(sessionKey, null);
    },
  });
}

export const ROLE_LABELS = { admin: 'Admin', operator: 'Operadora' } as const;

export function initials(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}
